import { deepImmerUpdate, getSavedState, Utils } from "@comact/crc";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import _ from "lodash";
import {
    IGroup, IGroups, IMyAccount, IOrganization, IOrganizations, IPartialUser, IPartialUsers,
    IPermissionDetails,
    IPermissionKey,
    IPermissionsDetails, IUser, IUserMSAL, IUsers
} from "./model";

declare global {
    interface IStoreState {
        myAccount: IMyAccount;
        myAccountOrganizations: IOrganizations;
        myAccountUsers: IUsers;
        userOrganizationId: string;
        organizationUsers: IUsers;
        organizationPartialUsers: IPartialUsers;
        organizationGroups: IGroups;
        userPermissions: IPermissionKey[];
        permissionsDetails: IPermissionDetails[]; // only used by cmoc (oper8 still uses userPermissions)
    }
}

const myAccount = createSlice({
    name: "myAccount",
    initialState: null as IMyAccount,
    reducers: {
        setMyAccountFromUserManager: (state, { payload }: PayloadAction<IMyAccount>) => (
            deepImmerUpdate(state, {
                id: payload.id,
                username: payload.username,
                phoneNumber: payload.phoneNumber,
                email: payload.email,
                password: null,
            })
        ),
        setUserFromMSAL: (state, { payload }: PayloadAction<IUserMSAL>) => {
            if (!payload) return null; // user logout

            return deepImmerUpdate(state, {
                id: payload.idTokenClaims?.oid,
                username: payload.username,
            } as IMyAccount);
        },
    },
});

const isEqualOrganization = (a: IOrganization, b: IOrganization) => a.modificationDate == b.modificationDate;

const myAccountOrganizations = createSlice({
    name: "myAccountOrganizations",
    initialState: null as IOrganizations,
    reducers: {
        setMyAccountOrganizations: (state, { payload }: PayloadAction<IOrganization[]>) => (
            Utils.slices.set({
                state,
                nextState: _.keyBy(payload, ({ id }) => id),
                isEqual: isEqualOrganization,
                useStats: "myAccountOrganizations",
            })
        ),
    },
});

const isEqualUser = (a: IUser | IPartialUser, b: IUser | IPartialUser) => a.modificationDate == b.modificationDate;

const myAccountUsers = createSlice({
    name: "myAccountUsers",
    initialState: null as IUsers,
    reducers: {
        setMyAccountUsers: (state, { payload }: PayloadAction<IUser[]>) => (
            Utils.slices.set({
                state,
                nextState: _.keyBy(payload, ({ id }) => id),
                isEqual: isEqualUser,
                useStats: "myAccountUsers",
            })
        ),
        patchMyAccountUsers: (state, { payload }: PayloadAction<IUser[]>) => (
            Utils.slices.patch({
                state,
                nextState: _.keyBy(payload, ({ id }) => id),
                isEqual: isEqualUser,
                useStats: "myAccountUsers",
            })
        ),
    },
});

const userOrganizationId = createSlice({
    name: "userOrganizationId",
    initialState: getSavedState("userOrganizationId", null as string),
    reducers: {
        clearUserOrganizationId: () => null,
    },
    extraReducers(builder) {
        // When only 1 org, we want to select it automatically
        builder.addCase(myAccountOrganizations.actions.setMyAccountOrganizations, (_state, { payload }) => {
            if (_.size(payload) == 1) return _.head(payload).id;
            return null;
        });
    },
});

const organizationUsers = createSlice({
    name: "organizationUsers",
    initialState: null as IUsers,
    reducers: {
        setOrganizationUsers: (state, { payload }: PayloadAction<IUser[]>) => (
            Utils.slices.set({
                state,
                nextState: _.keyBy(payload, ({ id }) => id),
                isEqual: isEqualUser,
                useStats: "organizationUsers",
            })
        ),
        patchOrganizationUsers: (state, { payload }: PayloadAction<IUser[]>) => (
            Utils.slices.patch({
                state,
                nextState: _.keyBy(payload, ({ id }) => id),
                isEqual: isEqualUser,
                useStats: "organizationUsers",
            })
        ),
    },
});

const organizationPartialUsers = createSlice({
    name: "organizationPartialUsers",
    initialState: null as IPartialUsers,
    reducers: {
        setOrganizationPartialUsers: (state, { payload }: PayloadAction<IPartialUser[]>) => (
            Utils.slices.set({
                state,
                nextState: _.keyBy(payload, ({ id }) => id),
                isEqual: isEqualUser,
                useStats: "organizationPartialUsers",
            })
        ),
        patchOrganizationPartialUsers: (state, { payload }: PayloadAction<IPartialUser[]>) => (
            Utils.slices.patch({
                state,
                nextState: _.keyBy(payload, ({ id }) => id),
                isEqual: isEqualUser,
                useStats: "organizationPartialUsers",
            })
        ),
    },
    extraReducers(builder) {
        builder.addCase(organizationUsers.actions.setOrganizationUsers, (state, { payload }) => (
            Utils.slices.set({
                state,
                nextState: _.chain(payload)
                    .map((data) => Utils.object.pick(data, "id", "email", "name", "modificationDate", "username", "deleted", "hidden", "isExternal", "groupIds"))
                    .keyBy(({ id }) => id)
                    .value(),
                isEqual: isEqualUser,
                useStats: "organizationPartialUsers",
            })
        ));
        builder.addCase(organizationUsers.actions.patchOrganizationUsers, (state, { payload }) => (
            Utils.slices.patch({
                state,
                nextState: _.chain(payload)
                    .map((data) => Utils.object.pick(data, "id", "email", "name", "modificationDate", "username", "deleted", "hidden", "isExternal", "groupIds"))
                    .keyBy(({ id }) => id)
                    .value(),
                isEqual: isEqualUser,
                useStats: "organizationPartialUsers",
            })
        ));
    },
});

const isEqualGroup = (a: IGroup, b: IGroup) => a.modificationDate == b.modificationDate;

const organizationGroups = createSlice({
    name: "organizationGroups",
    initialState: null as IGroups,
    reducers: {
        setOrganizationGroups: (state, { payload }: PayloadAction<IGroup[]>) => (
            Utils.slices.set({
                state,
                nextState: _.keyBy(payload, ({ id }) => id),
                isEqual: isEqualGroup,
                useStats: "organizationGroups",
            })
        ),
        patchOrganizationGroups: (state, { payload }: PayloadAction<IGroup[]>) => (
            Utils.slices.patch({
                state,
                nextState: _.keyBy(payload, ({ id }) => id),
                isEqual: isEqualGroup,
                useStats: "organizationGroups",
            })
        ),
        deleteOrganizationGroups: (state, { payload }: PayloadAction<string[]>) => (
            Utils.slices.delete({ state, keys: payload, useStats: "organizationGroups" })
        ),
    },
});

const userPermissions = createSlice({
    name: "userPermissions",
    initialState: null as IPermissionKey[],
    reducers: {
        setPermissions: (_state, { payload }: PayloadAction<IPermissionKey[]>) => payload,
    },
});

// Only used by cmoc (oper8 still uses userPermissions)
const permissionsDetails = createSlice({
    name: "permissionsDetails",
    initialState: null as IPermissionsDetails,
    reducers: {
        setPermissionsDetails: (state, { payload }: PayloadAction<IPermissionDetails[]>) => (
            Utils.slices.setWithoutEqualCheck({
                state,
                nextState: _.keyBy(payload, ({ type }) => type),
                useStats: "permissionsDetails",
            })
        ),
    },
});

export const actions = {
    ...myAccount.actions,
    ...userOrganizationId.actions,
    ...myAccountOrganizations.actions,
    ...myAccountUsers.actions,
    ...organizationUsers.actions,
    ...organizationPartialUsers.actions,
    ...organizationGroups.actions,
    ...userPermissions.actions,
    ...permissionsDetails.actions,
};

export default {
    [myAccount.name]: myAccount,
    [userOrganizationId.name]: userOrganizationId,
    [myAccountOrganizations.name]: myAccountOrganizations,
    [myAccountUsers.name]: myAccountUsers,
    [organizationUsers.name]: organizationUsers,
    [organizationPartialUsers.name]: organizationPartialUsers,
    [organizationGroups.name]: organizationGroups,
    [userPermissions.name]: userPermissions,
    [permissionsDetails.name]: permissionsDetails,
};